import { v4 as uuidv4 } from "uuid";
import {
    QuinnRuntimeEventType as EventType,
    type QuinnScopeMode,
    type QuinnPromptOptionKind,
    type QuinnRole,
    type QuinnTraceStepKind,
    type QuinnTraceStepStatus,
} from "./enums.ts";
import type { DocumentCategory, DocumentDirection, QuinnRatingEnum } from "../../../generated/enums.ts";

export type QuinnTraceStep = {
    id: string;
    kind: QuinnTraceStepKind;
    label: string;
    detail: string | null;
    status: QuinnTraceStepStatus;
    durationMs: number | null;
    payload: Record<string, unknown> | null;
};

export type QuinnTransactionSource = {
    description?: string;
    relation_name?: string;
    booking_date?: string;
    amount?: number;
    currency?: string;
    flow?: "inflow" | "outflow";
    generalLedgerAccountLabel?: string;
};

export type QuinnDocumentSource = {
    document_id?: string;
    business_id?: string;
    file_name?: string | null;
    document_category?: DocumentCategory | null;
    document_direction?: DocumentDirection | null;
    counterparty?: string | null;
    document_date?: string | null;
    scanned_fields?: {
        seller_name?: string;
        normalized_datetime?: string;
        date?: string;
        total_amount_payable?: {
            signed_amount?: number;
            currency?: string;
        };
    };
};

export type QuinnSourceBuckets = {
    transactions: QuinnTransactionSource[];
    documents: QuinnDocumentSource[];
    forecasts: Record<string, unknown>[];
};

export type QuinnPromptOption = {
    value: string;
    label: string;
    hint?: string | null;
};

export type QuinnInteractivePrompt = {
    id: string;
    kind: QuinnPromptOptionKind;
    question: string;
    helpText: string | null;
    options: QuinnPromptOption[];
    multiple: boolean;
    allowFreeText: boolean;
    answeredWith: string[] | null;
};

export type QuinnMessage = {
    id: string;
    role: QuinnRole;
    content: string;
    createdAt: string;
    isStreaming: boolean;
    trace: QuinnTraceStep[];
    sources: QuinnSourceBuckets;
    prompt: QuinnInteractivePrompt | null;
    error: string | null;
    runId: string | null;
    rating: QuinnRatingEnum | null;
};

export type QuinnScope = {
    mode: QuinnScopeMode;
    businessId: string | null;
    businessLabel: string;
};

export type QuinnChatSummary = {
    id: string;
    title: string;
    updatedAt: string;
    mode: QuinnScopeMode;
    businessId: string | null;
    businessLabel: string;
};

export type QuinnBusinessOption = {
    id: string;
    name: string;
};

export type QuinnPageContext = {
    path: string;
    label: string;
    businessId: string | null;
    businessName: string | null;
};

export type QuinnRuntimeEvent =
    | { type: typeof EventType.ChatStarted; chatId: string }
    | { type: typeof EventType.StepStarted; step: QuinnTraceStep }
    | {
          type: typeof EventType.StepFinished;
          stepId: string;
          status: QuinnTraceStepStatus;
          detail?: string | null;
          durationMs?: number;
          payload?: Record<string, unknown> | null;
      }
    | { type: typeof EventType.AnswerDelta; text: string }
    | {
          type: typeof EventType.AnswerFinished;
          runId?: string | null;
          sources?: QuinnSourceBuckets;
          prompt?: QuinnInteractivePrompt | null;
      }
    | { type: typeof EventType.Error; message: string };

export type QuinnSendRequest = {
    content: string;
    chatId: string | null;
    scope: QuinnScope;
    pageContext: QuinnPageContext;
    signal: AbortSignal;
};

export type QuinnRuntime = {
    send: (request: QuinnSendRequest) => AsyncGenerator<QuinnRuntimeEvent, void, unknown>;
    listChats: () => Promise<QuinnChatSummary[]>;
    openChat: (chatId: string) => Promise<QuinnMessage[]>;
    listBusinesses: () => Promise<QuinnBusinessOption[]>;
    rateAnswer: (runId: string, rating: QuinnRatingEnum | null) => Promise<void>;
};

export const EMPTY_SOURCES: QuinnSourceBuckets = {
    transactions: [],
    documents: [],
    forecasts: [],
};

export function createMessageId(): string {
    return uuidv4();
}
